/**
 * Typed client for the plan-lifecycle endpoints (backend second pass).
 *
 * Contract (align with the backend worker):
 *  - POST   /v1/plans                        {name, district_count} -> PlanSummary (201)
 *  - GET    /v1/plans                        -> PlanSummary[] (may be unimplemented)
 *  - GET    /v1/plans/{id}                   -> PlanDetail (assignments + stats)
 *  - PUT    /v1/plans/{id}/assignments       {assignments: {geoid: district}} -> {updated: n}
 *  - POST   /v1/plans/{id}/validate          -> ValidationReport
 *  - POST   /v1/plans/{id}/submit            -> SubmitResult | 422 with failure list
 *  - GET    /v1/tenant/block-populations     -> {geoids: [], populations: []} (compact arrays)
 *  - GET    /v1/tenant/reference-layers      -> [{kind, label, geojson}]
 *
 * Defensive posture: the backend is being implemented in parallel, so every
 * payload is coerced with defaults rather than hard-rejected. Callers
 * distinguish "not implemented yet" (HTTP 501 / 404 on the route) from real
 * failures via ApiError.status — see the PlanPanel fallbacks.
 */
import type { FeatureCollection } from 'geojson';
import { ApiError, apiBaseUrl, apiFetch, fetchWithAuth } from './api';

export type PlanStatus = 'draft' | 'submitted' | 'archived';

export interface PlanSummary {
  id: string;
  name: string;
  district_count: number;
  status: PlanStatus;
  updated_at: string;
}

export interface PlanDistrictStats {
  district: number;
  population: number;
  deviation_pct: number;
}

export interface PlanDetail extends PlanSummary {
  /** Full block-equivalency mapping: GEOID -> district index. */
  assignments: Record<string, number>;
  total_population: number;
  ideal_population: number;
  /** The author's explanation of the plan (null when none). */
  comments: string | null;
  /** Custom district names, one per district ("" = default); null when none. */
  district_labels: string[] | null;
}

/** Longest custom district name the API accepts. */
export const DISTRICT_LABEL_MAX = 60;

/** Display names: the plan's custom names over the defaults. */
export function mergeDistrictLabels(defaults: string[], custom: readonly string[] | null | undefined): string[] {
  return defaults.map((name, i) => (custom?.[i] ?? '').trim() || name);
}

function toLabelList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const labels = value.map((v) => (typeof v === 'string' ? v : ''));
  return labels.some((l) => l.trim()) ? labels : null;
}

/** Longest comment the API accepts (matches plans.comments / submissions.note). */
export const PLAN_COMMENTS_MAX = 2000;

export interface DistrictValidationResult {
  district: number;
  label: string;
  population: number;
  deviation_pct: number;
  contiguous: boolean;
  components: number;
  compactness_polsby_popper: number | null;
  failures: string[];
  warnings: string[];
}

export interface ValidationReport {
  plan_id: string;
  valid: boolean;
  failures: string[];
  warnings: string[];
  districts: DistrictValidationResult[];
}

export interface SubmitResult {
  submission_id: string | null;
  plan_id: string;
  snapshot_hash: string;
  submitted_at: string | null;
}

export interface BlockPopulations {
  geoids: string[];
  populations: number[];
}

/** A staff-uploaded district map (GET /v1/tenant/reference-layers). */
export interface ReferenceLayer {
  /** old_districts (current adopted map), draft, or historical. */
  kind: string;
  slug: string;
  label: string;
  description: string | null;
  /** ISO date (YYYY-MM-DD) or null. */
  effectiveDate: string | null;
  geojson: FeatureCollection;
}

/* ------------------------------------------------------------------ */
/* Coercion helpers (network boundary: coerce, don't crash)             */
/* ------------------------------------------------------------------ */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function toPlanStatus(value: unknown): PlanStatus {
  return value === 'submitted' || value === 'archived' ? value : 'draft';
}

function toPlanSummary(value: unknown): PlanSummary {
  const record = isRecord(value) ? value : {};
  return {
    id: toString(record.id, ''),
    name: toString(record.name, 'Untitled plan'),
    district_count: toNumber(record.district_count, 0),
    status: toPlanStatus(record.status),
    updated_at: toString(record.updated_at, ''),
  };
}

/**
 * Coerce a raw assignment value to a 0-based district index.
 *
 * The backend contract is 1-based: assignments cross the wire as integers in
 * [1, district_count] (see backend/app/validation.py::validate_assignment_inputs
 * and the plan serializers). The map paints by 0-based index, so this
 * subtracts one. Numeric-looking strings ("1".."N") and tenant labels resolve
 * the same way; anything else (including 0) is invalid and yields null.
 */
export function coerceDistrictIndex(
  value: unknown,
  districtCount: number,
  labels: string[] | undefined,
): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) {
    // 1-based wire format ("1".."N") -> 0-based index.
    if (value >= 1 && value <= districtCount) return value - 1;
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const parsed = Number(trimmed);
    // 1-based labels ("1".."N") are common in block-equivalency files.
    if (Number.isInteger(parsed) && parsed >= 1 && parsed <= districtCount) return parsed - 1;
    if (labels) {
      const idx = labels.findIndex((label) => label === trimmed);
      if (idx >= 0) return idx;
    }
  }
  return null;
}

function toAssignments(
  value: unknown,
  districtCount: number,
  labels: string[] | undefined,
): Record<string, number> {
  const assignments: Record<string, number> = {};
  if (!isRecord(value)) return assignments;
  for (const [geoid, district] of Object.entries(value)) {
    const index = coerceDistrictIndex(district, districtCount, labels);
    if (index !== null) assignments[geoid] = index;
  }
  return assignments;
}

export function toPlanDetail(value: unknown, districtCount: number, labels: string[] | undefined): PlanDetail {
  const summary = toPlanSummary(value);
  const record = isRecord(value) ? value : {};
  return {
    ...summary,
    assignments: toAssignments(record.assignments, districtCount || summary.district_count, labels),
    total_population: toNumber(record.total_population, 0),
    comments: typeof record.comments === 'string' ? record.comments : null,
    district_labels: toLabelList(record.district_labels),
    ideal_population: toNumber(record.ideal_population, 0),
  };
}

/** Messages from a list of strings or `{ code, message }` finding objects. */
function toMessageList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === 'string' ? item : isRecord(item) ? toString(item.message, '') : ''))
    .filter((m) => m.length > 0);
}

/**
 * One district's validation row. The API reports 1-based districts with
 * `polsby_popper` and `orphan_geoids`; the editor works in 0-based indices.
 * Older/alternate shapes (`compactness_polsby_popper`, `components`) are
 * still accepted.
 */
function toDistrictValidationResult(value: unknown, index: number): DistrictValidationResult {
  const record = isRecord(value) ? value : {};
  const compactness = record.polsby_popper ?? record.compactness_polsby_popper;
  const district1 = toNumber(record.district, index + 1);
  const orphans = Array.isArray(record.orphan_geoids) ? record.orphan_geoids.length : 0;
  const contiguous = typeof record.contiguous === 'boolean' ? record.contiguous : orphans === 0;
  return {
    district: district1 - 1,
    label: toString(record.label, `District ${district1}`),
    population: toNumber(record.population, 0),
    deviation_pct: toNumber(record.deviation_pct, 0),
    contiguous,
    components: toNumber(record.components, contiguous ? 1 : 0),
    compactness_polsby_popper:
      typeof compactness === 'number' && Number.isFinite(compactness) ? compactness : null,
    failures: toMessageList(record.failures),
    warnings: toMessageList(record.warnings),
  };
}

function toValidationReport(value: unknown): ValidationReport {
  const record = isRecord(value) ? value : {};
  const districts = Array.isArray(record.districts)
    ? record.districts.map((d, i) => toDistrictValidationResult(d, i))
    : [];
  const failures = toMessageList(record.hard_failures ?? record.failures);
  const passed = typeof record.passed === 'boolean' ? record.passed : record.valid === true;
  return {
    plan_id: toString(record.plan_id, ''),
    valid: passed && failures.length === 0,
    failures,
    warnings: toMessageList(record.warnings),
    districts,
  };
}

function isFeatureCollection(value: unknown): value is FeatureCollection {
  return (
    isRecord(value) &&
    value.type === 'FeatureCollection' &&
    Array.isArray(value.features)
  );
}

function toReferenceLayer(value: unknown): ReferenceLayer | null {
  if (!isRecord(value)) return null;
  const geojson = value.geojson;
  if (!isFeatureCollection(geojson)) return null;
  const kind = toString(value.kind, 'unknown');
  return {
    kind,
    slug: toString(value.slug, kind === 'old_districts' ? 'current' : kind),
    label: toString(value.label, 'Reference layer'),
    description: typeof value.description === 'string' ? value.description : null,
    effectiveDate: typeof value.effective_date === 'string' ? value.effective_date : null,
    geojson,
  };
}

/* ------------------------------------------------------------------ */
/* Endpoints                                                           */
/* ------------------------------------------------------------------ */

/** POST /v1/plans — create a draft plan. */
export async function createPlan(name: string, districtCount: number): Promise<PlanSummary> {
  const payload = await apiFetch<unknown>('/v1/plans', {
    method: 'POST',
    body: JSON.stringify({ name, district_count: districtCount }),
  });
  return toPlanSummary(payload);
}

/**
 * Starting-point template for POST /v1/plans/from-template.
 *
 * - blank: today's behavior, everything unassigned.
 * - current_districts: pre-filled from the jurisdiction's adopted districts.
 * - shared_plan: fork a shared plan link (bare token or full /p/<token> URL).
 */
export type PlanTemplate =
  | { type: 'blank' }
  | { type: 'current_districts' }
  | { type: 'shared_plan'; token: string };

/**
 * POST /v1/plans/from-template — create a draft plan from a template.
 *
 * Returns the full plan detail (assignments included) so the caller can
 * load it straight into the editor.
 */
export async function createPlanFromTemplate(
  name: string,
  districtCount: number,
  template: PlanTemplate,
  labels: string[] | undefined,
): Promise<PlanDetail> {
  const templatePayload =
    template.type === 'shared_plan'
      ? { type: template.type, token: template.token }
      : { type: template.type };
  const payload = await apiFetch<unknown>('/v1/plans/from-template', {
    method: 'POST',
    body: JSON.stringify({
      name,
      district_count: districtCount,
      template: templatePayload,
    }),
  });
  return toPlanDetail(payload, districtCount, labels);
}

/**
 * GET /v1/plans — list the tenant's plans.
 *
 * FALLBACK: this route was not in the backend's first-pass stubs; the
 * backend worker may or may not implement it. Callers treat 501/404 as
 * "listing unavailable" and fall back to the session's known plans.
 */
export async function listPlans(): Promise<PlanSummary[]> {
  return toPlanList(await apiFetch<unknown>('/v1/plans'));
}

/** GET /v1/plans/mine — the signed-in account's own plans, newest edit first. */
export async function listMyPlans(): Promise<PlanSummary[]> {
  return toPlanList(await apiFetch<unknown>('/v1/plans/mine'));
}

/** Accept both a bare array and the API's `{ plans: [...] }` envelope. */
function toPlanList(payload: unknown): PlanSummary[] {
  const items = Array.isArray(payload)
    ? payload
    : isRecord(payload) && Array.isArray(payload.plans)
      ? payload.plans
      : [];
  return items.map(toPlanSummary).filter((p) => p.id.length > 0);
}

/** GET /v1/plans/{id} — plan metadata + full assignments + stats. */
export async function getPlan(
  planId: string,
  districtCount: number,
  labels: string[] | undefined,
): Promise<PlanDetail> {
  const payload = await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}`);
  return toPlanDetail(payload, districtCount, labels);
}

/**
 * PUT /v1/plans/{id}/assignments — bulk upsert of changed assignments.
 * Only dirty (changed-since-last-save) entries are sent.
 *
 * The painting store tracks 0-based district indices; the backend contract
 * is 1-based ([1, district_count]), so indices are shifted on the way out.
 * Null values un-assign blocks (the backend deletes those rows).
 */
export async function putAssignments(
  planId: string,
  assignments: Record<string, number | null>,
): Promise<number> {
  const wire: Record<string, number | null> = {};
  for (const [geoid, district] of Object.entries(assignments)) {
    wire[geoid] = district === null ? null : district + 1;
  }
  const payload = await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}/assignments`, {
    method: 'PUT',
    body: JSON.stringify({ assignments: wire }),
  });
  const record = isRecord(payload) ? payload : {};
  return toNumber(record.updated, Object.keys(assignments).length);
}

/**
 * DELETE /v1/plans/{id} — delete one of your own draft plans. The server
 * refuses (409) for submitted plans, which are part of the public record.
 */
export async function deletePlan(planId: string): Promise<void> {
  await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}`, { method: 'DELETE' });
}

/** PUT /v1/plans/{id}/district-labels — custom district names ("" = default). */
export async function putDistrictLabels(planId: string, labels: string[]): Promise<void> {
  await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}/district-labels`, {
    method: 'PUT',
    body: JSON.stringify({ labels }),
  });
}

/** PUT /v1/plans/{id}/comments — save the author's comments (blank clears). */
export async function putPlanComments(planId: string, comments: string): Promise<string | null> {
  const payload = await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}/comments`, {
    method: 'PUT',
    body: JSON.stringify({ comments }),
  });
  return isRecord(payload) && typeof payload.comments === 'string' ? payload.comments : null;
}

/** POST /v1/plans/{id}/validate — full validation report. */
export async function validatePlan(planId: string): Promise<ValidationReport> {
  const payload = await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}/validate`, {
    method: 'POST',
  });
  return toValidationReport(payload);
}

/**
 * POST /v1/plans/{id}/submit — freeze a canonical snapshot.
 *
 * A 422 carries the hard-failure list in the body, so this uses its own
 * fetch plumbing (apiFetch discards non-string detail bodies). Throws
 * SubmitRejectedError on 422; ApiError otherwise.
 */
export class SubmitRejectedError extends Error {
  readonly failures: string[];
  readonly warnings: string[];

  constructor(failures: string[], warnings: string[]) {
    super(`Plan submission rejected: ${failures.join('; ') || 'validation failed'}`);
    this.name = 'SubmitRejectedError';
    this.failures = failures;
    this.warnings = warnings;
  }
}

/**
 * Extract human-readable failure messages from a 422 body.
 *
 * The backend sends `detail.hard_failures` as [{code, message, details}];
 * older shapes (top-level `failures` array of strings) are still accepted.
 */
function toFailureMessages(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      out.push(item);
    } else if (isRecord(item) && typeof item.message === 'string') {
      out.push(item.message);
    }
  }
  return out;
}

export async function submitPlan(planId: string): Promise<SubmitResult> {
  let response: Response;
  try {
    // The endpoint requires a session and a JSON body ({ note? }).
    response = await fetchWithAuth(`${apiBaseUrl()}/v1/plans/${encodeURIComponent(planId)}/submit`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    throw new ApiError(0, 'network_error', `Could not reach the backend: ${message}`);
  }

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? (JSON.parse(text) as unknown) : undefined;
  } catch {
    throw new ApiError(response.status, 'invalid_json', 'Backend returned a non-JSON response body.');
  }

  if (response.status === 422) {
    const record = isRecord(body) ? body : {};
    const detail = isRecord(record.detail) ? record.detail : {};
    const failures = toFailureMessages(
      detail.hard_failures ?? record.failures ?? record.detail,
    );
    const warnings = toStringList(detail.warnings ?? record.warnings);
    throw new SubmitRejectedError(failures, warnings);
  }

  if (!response.ok) {
    const record = isRecord(body) ? body : {};
    const detail =
      typeof record.detail === 'string' ? record.detail : response.statusText;
    throw new ApiError(response.status, `http_${response.status}`, detail || 'Request failed');
  }

  const record = isRecord(body) ? body : {};
  return {
    submission_id:
      typeof record.submission_id === 'string' ? record.submission_id : null,
    plan_id: toString(record.plan_id, planId),
    snapshot_hash: toString(record.snapshot_hash, ''),
    submitted_at: typeof record.submitted_at === 'string' ? record.submitted_at : null,
  };
}

/** GET /v1/tenant/block-populations — compact parallel arrays, fetched once per session. */
export async function fetchBlockPopulations(): Promise<BlockPopulations> {
  const payload = await apiFetch<unknown>('/v1/tenant/block-populations');
  const record = isRecord(payload) ? payload : {};
  const geoids = toStringList(record.geoids);
  const populations = Array.isArray(record.populations)
    ? record.populations.map((p) => (typeof p === 'number' && Number.isFinite(p) ? p : 0))
    : [];
  // Guard against ragged arrays: truncate to the shorter length.
  const length = Math.min(geoids.length, populations.length);
  return { geoids: geoids.slice(0, length), populations: populations.slice(0, length) };
}

/** GET /v1/tenant/reference-layers — GeoJSON reference overlays (e.g. old districts). */
export async function fetchReferenceLayers(): Promise<ReferenceLayer[]> {
  const payload = await apiFetch<unknown>('/v1/tenant/reference-layers');
  if (!Array.isArray(payload)) return [];
  const layers: ReferenceLayer[] = [];
  for (const item of payload) {
    const layer = toReferenceLayer(item);
    if (layer) layers.push(layer);
  }
  return layers;
}

/** True when the backend route is not implemented yet (parallel-work fallback signal). */
export function isNotImplemented(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 501 || error.status === 404);
}

/**
 * True when the plans listing rejected the caller as non-staff (HTTP 401).
 * The listing is staff-only; anonymous painting sessions fall back to
 * session-known plans, exactly as they do for a 501.
 */
export function isListingForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}
